import {
  Capacitor,
  registerPlugin,
  type PluginListenerHandle,
} from "@capacitor/core";
import posthog from "posthog-js";
import { captureError } from "@/lib/observability";

// Push notifications del wrapper móvil (Capacitor, Android + iOS). Sólo corre
// dentro de la app nativa — en el browser/Electron es un no-op total (ahí el
// caso "app abierta en background" ya lo cubre system-notifications.ts; el
// caso "app cerrada" quedó deliberadamente afuera para web).
//
// Flujo: con sesión activa (ver use-native-push.ts) pedimos permiso, sacamos
// el token de FCM vía el plugin FirebaseMessaging (en iOS canjea el token de
// APNs por uno de FCM — el server habla UN solo protocolo) y lo posteamos a
// /api/push/register. El tap en una notificación navega a la ruta interna que
// viajó en `data.url` (la arma el server, src/server/push/send.ts).
//
// OJO: el plugin se toma con `registerPlugin` de @capacitor/core, NO
// importando `@capacitor-firebase/messaging` — el entry JS de ese paquete
// arrastra su implementación WEB, que importa `firebase/messaging` (SDK que
// no tenemos ni queremos: acá el push web no existe) y rompe el build. El
// proxy nativo alcanza: en Android/iOS llama a la implementación real (el
// código nativo del paquete sí está linkeado en native/<flavor>/); en web
// nunca se invoca (todo está gateado por isNativeApp()).

type NativePermissionState =
  | "granted"
  | "denied"
  | "prompt"
  | "prompt-with-rationale";

interface FirebaseMessagingPlugin {
  checkPermissions(): Promise<{ receive: NativePermissionState }>;
  requestPermissions(): Promise<{ receive: NativePermissionState }>;
  getToken(): Promise<{ token: string }>;
  deleteToken(): Promise<void>;
  addListener(
    eventName: "tokenReceived",
    listener: (event: { token: string }) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    eventName: "notificationActionPerformed",
    listener: (event: {
      notification?: { data?: Record<string, string> };
    }) => void,
  ): Promise<PluginListenerHandle>;
}

const FirebaseMessaging =
  registerPlugin<FirebaseMessagingPlugin>("FirebaseMessaging");

const PUSH_TOKEN_STORAGE_KEY = "vortigon.push.token";

export function isNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------
// Permiso de notificaciones a nivel SO, para el setup del SOS (permission-
// setup.tsx). En el webview del wrapper NO existe la web Notification API, así
// que el estado/pedido van por el plugin FCM (en Android 13+ muestra el prompt
// de POST_NOTIFICATIONS; en versiones previas concede solo). Best-effort como
// todo lo de push: ante cualquier fallo devuelven "prompt" (= re-intentable),
// nunca tiran.
// ---------------------------------------------------------------------

type SimplePermissionState = "granted" | "denied" | "prompt";

function simplify(state: NativePermissionState): SimplePermissionState {
  return state === "granted" || state === "denied" ? state : "prompt";
}

export async function checkNativeNotificationPermission(): Promise<SimplePermissionState> {
  try {
    return simplify((await FirebaseMessaging.checkPermissions()).receive);
  } catch {
    return "prompt";
  }
}

export async function requestNativeNotificationPermission(): Promise<SimplePermissionState> {
  try {
    return simplify((await FirebaseMessaging.requestPermissions()).receive);
  } catch {
    return "prompt";
  }
}

async function postToken(token: string): Promise<void> {
  const res = await fetch("/api/push/register", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      token,
      platform: Capacitor.getPlatform(), // "ios" | "android"
      flavor: "app",
    }),
  });
  if (!res.ok) throw new Error(`push register falló: ${res.status}`);
  try {
    window.localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token);
  } catch {}
}

export interface NativePushHandle {
  cleanup: () => void;
}

/**
 * Pide permiso, registra el token y engancha los listeners (rotación de token,
 * tap en notificación). Llamar SOLO en nativo y con sesión activa. Devuelve el
 * handle para desmontar los listeners. Best-effort: cualquier fallo se loguea
 * y la app sigue — push es accesorio, nunca bloquea el uso.
 */
export async function setupNativePush(
  onNavigate: (url: string) => void,
): Promise<NativePushHandle | null> {
  if (!isNativeApp()) return null;
  try {
    const perm = await FirebaseMessaging.requestPermissions();
    if (perm.receive !== "granted") {
      posthog.capture("push_permission_denied", {
        platform: Capacitor.getPlatform(),
      });
      return null;
    }

    const { token } = await FirebaseMessaging.getToken();
    await postToken(token);
    posthog.capture("push_registered", { platform: Capacitor.getPlatform() });

    // FCM puede rotar el token en cualquier momento → re-registrar.
    const tokenListener = await FirebaseMessaging.addListener(
      "tokenReceived",
      (event) => {
        // Si la rotación no se re-registra, el celular deja de recibir
        // despachos PARA SIEMPRE sin ninguna señal: reportamos el fallo.
        if (event.token) {
          void postToken(event.token).catch((err: unknown) =>
            captureError(err, {
              source: "push.token_rotation",
              tags: { background: true },
            }),
          );
        }
      },
    );

    // Tap en la notificación (app en background o cerrada): navegar a la ruta
    // interna que mandó el server. Sólo rutas relativas — un data.url absoluto
    // no nos saca de la app.
    const tapListener = await FirebaseMessaging.addListener(
      "notificationActionPerformed",
      (event) => {
        const url = event.notification?.data?.url;
        if (url && url.startsWith("/")) onNavigate(url);
      },
    );

    return {
      cleanup: () => {
        void tokenListener.remove();
        void tapListener.remove();
      },
    };
  } catch (err) {
    // El operador cree que el celular le va a avisar los despachos: si el
    // registro falla, no le llega nada nunca — eso tiene que sonar.
    console.warn("[push] setup falló:", err);
    void captureError(err, {
      source: "push.native_setup",
      tags: { background: true },
    });
    return null;
  }
}

/**
 * Baja del token al hacer logout: borra la fila del server y el token local
 * (deleteToken — el próximo login mintea uno fresco). Best-effort y acotado:
 * el logout nunca se demora ni se rompe por esto.
 */
export async function teardownNativePush(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    let token: string | null = null;
    try {
      token = (await FirebaseMessaging.getToken()).token;
    } catch {
      try {
        token = window.localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
      } catch {}
    }
    if (token) {
      await fetch("/api/push/register", {
        method: "DELETE",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
      }).catch(() => {});
    }
    await FirebaseMessaging.deleteToken().catch(() => {});
    try {
      window.localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
    } catch {}
  } catch {
    // Cualquier otro fallo: no es fatal.
  }
}
